import { useState } from "react";
import { Loader2, ExternalLink, ChevronDown, Check } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/frontend/components/ui/button";
import { CopyField } from "@/frontend/components/ui/copy-field";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/frontend/components/ui/collapsible";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/frontend/components/ui/dialog";
import { ClientLogo } from "@/frontend/components/shared/ClientBrand";
import { displayClientName } from "@/frontend/utils/client-brand";
import { docsUrl } from "@/lib/docs";
import { grantedScopeRows } from "@/frontend/utils/oauth-scopes";
import { useConnectedAppsQuery } from "@/frontend/queries/dashboard/settings";
import { useRevokeConnectedAppMutation } from "@/frontend/mutations/dashboard/settings";
import type { ConnectedApp } from "@/server/schema/connected-apps";
import { formatDayMonthYear } from "@/frontend/utils/shared";

/** One connected assistant, expandable to reveal granted permissions and connection details. */
function ConnectedAppRow({ app, onRevoke }: { app: ConnectedApp; onRevoke: (v: { consentId: string; name: string }) => void }) {
  const [open, setOpen] = useState(false);
  const name = displayClientName(app.client_name);
  const rows = grantedScopeRows(app.scopes);

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="rounded-lg border border-border/60">
      <div className="flex items-center justify-between gap-3 p-3">
        <CollapsibleTrigger className="flex flex-1 items-center gap-3 text-left">
          <ClientLogo name={app.client_name} logoUrl={app.client_icon} />
          <div className="min-w-0">
            <p className="text-sm font-medium">{name}</p>
            <p className="truncate text-xs text-muted-foreground">
              {rows.length ? `${rows.length} ${rows.length === 1 ? "permission" : "permissions"}` : "Identity only"}
              {app.granted_at ? ` · connected ${formatDayMonthYear(app.granted_at)}` : ""}
            </p>
          </div>
        </CollapsibleTrigger>
        <Button variant="outline" size="sm" onClick={() => onRevoke({ consentId: app.consent_id, name })}>
          Revoke
        </Button>
        <CollapsibleTrigger className="shrink-0" aria-label={open ? "Collapse" : "Expand"}>
          <ChevronDown className={`size-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
        </CollapsibleTrigger>
      </div>

      <CollapsibleContent>
        <div className="flex flex-col gap-4 border-t border-border/60 px-3 py-4">
          <div className="flex flex-col gap-3">
            <p className="text-xs font-medium uppercase tracking-wide text-foreground">What it can do</p>
            {rows.length === 0 ? (
              <p className="text-sm text-muted-foreground">Sees who you are and your workspace. No other access.</p>
            ) : (
              rows.map((row) => (
                <div key={row.key} className="flex items-start gap-2.5">
                  <Check className="mt-0.5 size-4 shrink-0 text-primary" />
                  <div>
                    <p className="text-sm font-medium">{row.label}</p>
                    <p className="text-xs text-muted-foreground">{row.description}</p>
                  </div>
                </div>
              ))
            )}
          </div>

          <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-1.5 text-xs">
            <dt className="text-muted-foreground">Connected</dt>
            <dd>{formatDayMonthYear(app.granted_at)}</dd>
            <dt className="text-muted-foreground">Last updated</dt>
            <dd>{formatDayMonthYear(app.updated_at)}</dd>
            <dt className="text-muted-foreground">Client ID</dt>
            <dd className="font-mono break-all">{app.client_id}</dd>
          </dl>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

/** Settings section for the Medblocks MCP server: how to connect an AI assistant, and which are connected. */
export function McpServerSection() {
  const { data: apps, isLoading } = useConnectedAppsQuery();
  const revoke = useRevokeConnectedAppMutation();
  const [confirming, setConfirming] = useState<{ consentId: string; name: string } | null>(null);

  const mcpUrl = `${window.location.origin}/mcp`;

  return (
    <div className="flex flex-col gap-8">
      <div>
        <h2 className="text-lg font-semibold">MCP server</h2>
        <p className="text-sm text-muted-foreground">
          Connect an AI assistant to your workspace so it can find your health systems, pull your records, and
          answer questions about them.
        </p>
      </div>

      {/* How to connect */}
      <div className="flex flex-col gap-3 rounded-lg border border-border/60 p-4">
        <p className="text-sm font-medium">Connect an AI assistant</p>
        <p className="text-sm text-muted-foreground">
          Add this URL as a custom MCP server in Claude, ChatGPT, Claude Code, or Codex, then sign in and choose
          what it can access.
        </p>
        <CopyField label="MCP server URL" value={mcpUrl} />
        <a
          href={docsUrl("/docs/mcp")}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
        >
          Setup guides for Claude Code, Codex, ChatGPT, and Claude
          <ExternalLink className="size-3.5" />
        </a>
      </div>

      {/* Connected clients */}
      <div className="flex flex-col gap-3">
        <p className="text-sm font-medium">Connected AI assistants</p>
        {isLoading ? (
          <Loader2 className="size-5 animate-spin text-muted-foreground" />
        ) : (apps ?? []).length === 0 ? (
          <p className="text-sm text-muted-foreground">Nothing connected yet.</p>
        ) : (
          <div className="flex flex-col gap-3">
            {(apps ?? []).map((app) => (
              <ConnectedAppRow key={app.consent_id} app={app} onRevoke={setConfirming} />
            ))}
          </div>
        )}
      </div>

      <Dialog open={confirming !== null} onOpenChange={(open) => { if (!open) setConfirming(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revoke access for {confirming?.name}?</DialogTitle>
            <DialogDescription>
              It loses access to your workspace. Access already issued expires within an hour. You can grant
              access again later by reconnecting.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirming(null)}>Cancel</Button>
            <Button
              variant="destructive"
              disabled={revoke.isPending}
              onClick={() => {
                if (!confirming) return;
                revoke.mutate({ consent_id: confirming.consentId }, {
                  onSuccess: () => { toast.success("Access revoked"); setConfirming(null); },
                  onError: (err) => toast.error(err.message),
                });
              }}
            >
              {revoke.isPending ? <Loader2 className="size-4 animate-spin" /> : "Revoke"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
